/**
 * The reference of a failed request (`errorRef`): what a user copies out of an
 * error message and a developer searches the backend log for. Digits and
 * capitals without the look-alikes `0 O 1 I L`.
 */
export const ERROR_REF_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";

export const ERROR_REF_LENGTH = 8;

const errorRefCharacters = new Set<string>(ERROR_REF_ALPHABET);

/** Whether `value` is a reference in its raw form, as it is on the wire. */
export function isErrorRef(value: unknown): value is string {
  if (typeof value !== "string" || value.length !== ERROR_REF_LENGTH) {
    return false;
  }
  for (const character of value) {
    if (!errorRefCharacters.has(character)) {
      return false;
    }
  }
  return true;
}

/**
 * The form of a reference that people read and that the backend logs:
 * `K7QF2M9X` becomes `K7QF-2M9X`. Anything that is not a raw reference is
 * returned unchanged.
 */
export function formatErrorRef(errorRef: string): string {
  if (!isErrorRef(errorRef)) {
    return errorRef;
  }
  const half = ERROR_REF_LENGTH / 2;
  return `${errorRef.slice(0, half)}-${errorRef.slice(half)}`;
}

/**
 * Start of the `message` a client receives when the backend masks error
 * messages. The reference follows in parentheses.
 */
export const MASKED_ERROR_MESSAGE = "Request failed";

/** The `message` of a masked error: the fixed text and the reference. */
export function maskedErrorMessage(errorRef: string): string {
  return `${MASKED_ERROR_MESSAGE} (reference ${formatErrorRef(errorRef)})`;
}
